import React, { useState, useCallback, useEffect, useRef } from 'react';
import GenService, { solveBuild } from '../services/GenService';
import GenConfig from '../core/GenConfig';
import { trackEvent } from '../services/analytics';
import { recordCapGateEngaged, recordCapGateShown } from '../services/metrics';
import { useOverlay } from '../contexts/OverlayContext';
import { useAuth } from '../contexts/AuthContext';
import CapGate, { capGateTitle, VARIANT_NUDGE } from '../components/generator/CapGate';
import { NOTIFICATION } from '../components/NotificationSystem';
import { applySolverBuildPayload } from '../utils/applySolverBuildPayload';
import { normTagIds } from '../core/criteriaTags';
import { describeInitialBuildValidity } from '../core/initialBuildValidity';
import { WORKER_SCRIPT_UNAVAILABLE } from '../core/localSolve';
import { markPageBusy, noteLocalSolverLost } from '../core/pageHealth';
import { notifyStaleLocalSolve } from './useStaleReload';
import { enrichLocalReport } from '../core/localSolve/report';
import { pushActiveAnonBuild } from '../services/anonSync';
import { CODE_SERVER_SOLVE_OFF } from './useGenerationJob';

/**
 * Guest generations in a day before the log-in *suggestion* pops up, and the
 * localStorage keys that count them. The suggestion is dismissible and blocks
 * nothing — the hard stop is the backend's anti-bot cap (70/day, see
 * backend/gen/rate_limit.py), which is a different, much later popup.
 *
 * Counted in the browser rather than served by the backend on purpose: the cap
 * has to be un-cheatable, but a suggestion does not, and asking the server for
 * it would mean widening the generate contract just to schedule a popup.
 */
const GUEST_NUDGE_AFTER_GENS = 5;
const GUEST_GENS_KEY = 'dofgen_guest_gens';
const GUEST_NUDGE_KEY = 'dofgen_nudge_shown';

// Fallback used when a build has no level set yet. Mirrors the same constant
// in Generator.js's former copy and hooks/useBuildPanelControls.js /
// useCriteriaAndScore.js (cluster D/B) — each hook keeps its own trivial copy
// rather than threading it through as a cross-hook dependency.
const DEFAULT_LVL = 200;

const MSG_SERVER_UNREACHABLE = { fr: 'Impossible de joindre le serveur. Réessayez plus tard.', en: 'Could not reach server. Try again later.', de: 'Server nicht erreichbar. Versuche es später erneut.', es: 'No se ha podido conectar con el servidor. Inténtalo más tarde.', pt: 'Não foi possível conectar ao servidor. Tente mais tarde.' };
const MSG_GEN_ERROR = { fr: 'Erreur serveur', en: 'Server error', de: 'Serverfehler', es: 'Error del servidor', pt: 'Erro do servidor' };
const MSG_GEN_STREAM_END = { fr: 'Génération terminée. Score :', en: 'Generation finished. Score:', de: 'Generierung abgeschlossen. Score:', es: 'Generación terminada. Puntuación:', pt: 'Geração concluída. Pontuação:' };
const MSG_SERVICE_UNAVAILABLE = { fr: 'Service temporairement indisponible.', en: 'Service temporarily unavailable.', de: 'Dienst vorübergehend nicht verfügbar.', es: 'Servicio temporalmente no disponible.', pt: 'Serviço temporariamente indisponível.' };
// Not "temporarily unavailable": nothing is coming back in a minute, and the
// only thing that helps is a different browser. Says that and stops — why we
// cannot solve it for them instead is our problem, not theirs.
const MSG_SERVER_SOLVE_OFF = { fr: "Votre navigateur ne peut pas exécuter le générateur. Mettez-le à jour ou essayez-en un autre.", en: "Your browser can't run the generator. Update it, or try a different one.", de: 'Dein Browser kann den Generator nicht ausführen. Aktualisiere ihn oder probiere einen anderen.', es: 'Tu navegador no puede ejecutar el generador. Actualízalo o prueba con otro.', pt: 'O seu navegador não consegue executar o gerador. Atualize-o ou tente outro.' };
const MSG_DAILY_QUOTA = { fr: 'Limite quotidienne de générations atteinte. Réessayez demain.', en: 'Daily generation limit reached. Try again tomorrow.', de: 'Tageslimit an Generierungen erreicht. Versuche es morgen erneut.', es: 'Límite diario de generaciones alcanzado. Inténtalo mañana.', pt: 'Limite diário de gerações atingido. Tente amanhã.' };
// Mirrors the backend defaults in gen/rate_limit.py; only used if the 429
// payload somehow arrives without its own `limit`.
const GUEST_LIMIT_DEFAULT = 50;
const GUEST_WEEKLY_LIMIT_DEFAULT = 350;

/** Today, UTC — the same day boundary the backend's quota buckets use. */
const utcDay = () => new Date().toISOString().slice(0, 10);

/**
 * Count one guest generation and return the running total when that makes the
 * suggestion due, else 0.
 *
 * Only reports "due" once per day: being asked to log in after every
 * generation is nagging, and someone who said "later" this morning has not
 * changed their mind by lunch. Storage being unavailable means no suggestion
 * at all — failing that way round can only under-prompt.
 */
function noteGuestGeneration() {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return 0;
        const today = utcDay();
        const [day, raw] = (window.localStorage.getItem(GUEST_GENS_KEY) || '').split(':');
        const count = (day === today ? Number(raw) || 0 : 0) + 1;
        window.localStorage.setItem(GUEST_GENS_KEY, `${today}:${count}`);
        if (count < GUEST_NUDGE_AFTER_GENS) return 0;
        if (window.localStorage.getItem(GUEST_NUDGE_KEY) === today) return 0;
        return count;
    } catch (_err) {
        return 0;
    }
}

/**
 * Burn today's suggestion. Separate from the counting above because the popup
 * is raised when the run *finishes* — a run that errors out should leave the
 * suggestion available for the next one rather than spending it silently.
 */
function markGuestNudgeShown() {
    try {
        window.localStorage.setItem(GUEST_NUDGE_KEY, utcDay());
    } catch (_err) {
        // Nothing to do: at worst the suggestion shows once more.
    }
}
// Quota notifications stay on screen a little longer than the default (5 s).
const QUOTA_NOTIF_DURATION_MS = 8000;
const MSG_GEN_STOP = { fr: 'Génération arrêtée.', en: 'Generation stopped.', de: 'Generierung gestoppt.', es: 'Generación detenida.', pt: 'Geração interrompida.' };

/**
 * Generation run control for the Generator page — `generate`/`handleStop`, the
 * live score/summary state consumed by the still-inline render layer (cluster
 * F: `renderElement`/`renderFixedGeneratorBar`/`renderGrid`), and the cleanup
 * around an in-flight run. See docs/generator-slicing.md cluster A.
 *
 * `generationJob` (the return value of `useGenerationJob()`) is NOT created
 * inside this hook — Generator.js still calls `useGenerationJob()` directly
 * and passes the result in here. That's because `isGenerationActive` (derived
 * from `generationJob.isGenerating`) is needed by cluster B
 * (`useCriteriaAndScore`) and cluster E (`useBuildIdentityActions`), both of
 * which are called *before* this hook in Generator.js so that *their* outputs
 * (`setCriteriaTagIds`, `blockingInvalidBuildReasonText`, `lvl`) can be
 * threaded into this hook as params. Moving the `useGenerationJob()` call itself in here would create
 * a call-order cycle (A needs B/D/E's outputs; B/E need A's
 * `isGenerationActive`). This hook re-derives the `start`/`stop`/`reset`
 * aliases from the passed-in `generationJob` exactly as Generator.js used to.
 *
 * `isServerUpdateRef`: a plain ref SHARED with cluster D and cluster F (both
 * still inline in Generator.js) — created and owned by Generator.js, passed
 * in here as a param. This hook only reads/writes `.current` on that same
 * instance (in `applyBuildFromPayload`); it must never create its own ref,
 * or F's reads of `isServerUpdateRef.current` inside `renderElement` would go
 * stale.
 *
 * `isGenerationActiveRef` is internal to this hook: it lets a preview that
 * lands after the run has ended see that it has, without re-creating
 * `generate` on every status tick.
 */
export function useGenerationRun({
    build,
    setBuild,
    genConfig,
    lang,
    showNotification,
    canEditGenerator,
    isGenerationActive,
    generationJob,
    blockingInvalidBuildReasonText,
    lvl,
    setCriteriaTagIds,
    isServerUpdateRef,
}) {
    const { showOverlay, hideOverlay } = useOverlay();
    const { isAuthenticated } = useAuth();
    const isGenerationActiveRef = useRef(false);
    // Guest generations counted at the start of a run, spent when it finishes:
    // 0 when the suggestion is not due (logged in, under the threshold, or
    // already shown today).
    const pendingNudgeCountRef = useRef(0);
    const canGenerate = canEditGenerator && !isGenerationActive && !blockingInvalidBuildReasonText;

    const [liveGenSoftScore, setLiveGenSoftScore] = useState(null);
    const [liveGenMaximizeScore, setLiveGenMaximizeScore] = useState(null);
    const [topCandidates, setTopCandidates] = useState(null);
    const [topSetCandidates, setTopSetCandidates] = useState(null);
    const [genSummaryOpen, setGenSummaryOpen] = useState(false);
    const [genSummaryExpanded, setGenSummaryExpanded] = useState(false);
    const [liveMetrics, setLiveMetrics] = useState(null);
    const [finalMetrics, setFinalMetrics] = useState(null);
    const [scoreTimeSeries, setScoreTimeSeries] = useState([]);
    const [elitePool, setElitePool] = useState(null);
    const [workerMetrics, setWorkerMetrics] = useState(null);
    const [aggregateMetrics, setAggregateMetrics] = useState(null);
    const [isGenSummaryLive, setIsGenSummaryLive] = useState(false);

    const startGenerationJob = generationJob.start;
    const stopGenerationJob = generationJob.stop;
    const resetGenerationJob = generationJob.reset;

    useEffect(() => {
        isGenerationActiveRef.current = isGenerationActive;
        // While this is up, nothing may reload the page underneath it — a
        // stale-version reload would throw away a generation the user is
        // waiting on, and paid for.
        if (!isGenerationActive) return undefined;
        markPageBusy(true);
        return () => markPageBusy(false);
    }, [isGenerationActive]);

    // Leaving the page stops the run: the abort reaches the workers.
    useEffect(() => () => {
        resetGenerationJob();
    }, [resetGenerationJob]);

    const applyBuildFromPayload = useCallback((newBuildData, currentBuild, isFinal = false) => {
        if (!newBuildData) return;
        const updatedBuild = applySolverBuildPayload({
            solverBuildPayload: newBuildData,
            baseBuild: currentBuild,
        });
        if (!updatedBuild) return;
        isServerUpdateRef.current = true;
        setBuild(updatedBuild);
        if (isFinal) {
            setCriteriaTagIds(normTagIds(newBuildData.criteriaTags ?? currentBuild?.criteriaTags));
            // Anonymous builds auto-publish, and a finished generation is the
            // moment a build becomes worth showing. setBuild has already written
            // the entry synchronously, and the user is still looking at the
            // result, so this request completes rather than racing a teardown.
            pushActiveAnonBuild();
        }
        setTimeout(() => { isServerUpdateRef.current = false; }, 100);
    }, [setBuild, setCriteriaTagIds, isServerUpdateRef]);

    const formatGenerationScore = useCallback((rawScore) => {
        const parsed = typeof rawScore === 'number' ? rawScore : parseFloat(rawScore || 0);
        return Number.isFinite(parsed) ? parsed.toFixed(1) : '0.0';
    }, []);

    const ingestLiveScores = useCallback((payload) => {
        if (!payload || typeof payload !== 'object') return;
        const softScore = payload.score ?? payload.result?.score;
        const maximizeScore = payload.maximize_score ?? payload.result?.maximize_score;
        if (typeof softScore === 'number' && Number.isFinite(softScore)) setLiveGenSoftScore(softScore);
        if (typeof maximizeScore === 'number' && Number.isFinite(maximizeScore)) setLiveGenMaximizeScore(maximizeScore);
    }, []);

    /**
     * Hand a finished solve's report to the summary panel.
     *
     * The run has it all at once, so this is the one place it lands. `isLive`
     * goes false here rather than after hydrate, because the search really is
     * over — what follows is only resolving names and icons.
     */
    const publishLocalReport = useCallback(async (report) => {
        if (!report) {
            setIsGenSummaryLive(false);
            return;
        }
        const enriched = await enrichLocalReport(report, (itemIds, setIds) =>
            GenService.resolveSolveNames(itemIds, setIds),
        );
        if (enriched?.metrics) setFinalMetrics(enriched.metrics);
        if (Array.isArray(enriched?.scoreTimeSeries)) setScoreTimeSeries(enriched.scoreTimeSeries);
        if (Array.isArray(enriched?.workerMetrics)) setWorkerMetrics(enriched.workerMetrics);
        if (Array.isArray(enriched?.elitePool)) setElitePool(enriched.elitePool);
        if (enriched?.candidates) setTopCandidates(enriched.candidates);
        if (enriched?.setCandidates) setTopSetCandidates(enriched.setCandidates);
        // One search, one set of counters: there is no separate per-worker
        // breakdown to aggregate.
        if (enriched?.metrics) setAggregateMetrics(enriched.metrics);
        setIsGenSummaryLive(false);
        setGenSummaryOpen(true);
    }, []);

    /**
     * Guest hit the free-tier cap. Shown as a modal rather than a toast so the
     * Discord button sits one click from the message — this is the app's only
     * paywall moment. Also opens the cap-gate outcome funnel for this session.
     */
    const showCapGate = useCallback((period, limit) => {
        // The gate has just made the same case, far more forcefully.
        pendingNudgeCountRef.current = 0;
        recordCapGateShown();
        trackEvent('cap_gate_shown', { period });
        const nextPath = `${window.location.pathname}${window.location.search}`;
        showOverlay(
            [
                <h6>{capGateTitle(lang)}</h6>,
                <CapGate
                    lang={lang}
                    period={period}
                    limit={limit}
                    nextPath={nextPath}
                    onDismiss={hideOverlay}
                />,
            ],
            { compact: true },
        );
    }, [showOverlay, hideOverlay, lang]);

    /**
     * Suggest logging in, after a guest's Nth generation of the day.
     *
     * Raised on the finished run rather than the start of one: the pitch is
     * "save this build and share it", which only means anything once there is
     * a build on screen — and a modal over a running generation would hide the
     * thing the person is waiting for. Nothing is blocked either way.
     */
    const showLoginNudge = useCallback((count) => {
        markGuestNudgeShown();
        trackEvent('login_nudge_shown');
        const nextPath = `${window.location.pathname}${window.location.search}`;
        showOverlay(
            [
                <h6>{capGateTitle(lang, VARIANT_NUDGE)}</h6>,
                <CapGate
                    lang={lang}
                    variant={VARIANT_NUDGE}
                    count={count}
                    nextPath={nextPath}
                    onDismiss={hideOverlay}
                />,
            ],
            { compact: true },
        );
    }, [showOverlay, hideOverlay, lang]);

    const generate = useCallback(async () => {
        if (!canGenerate && !isGenerationActive) return;
        // Trying again after being capped is engagement, not abandonment.
        recordCapGateEngaged();
        if (isGenerationActive) {
            await stopGenerationJob();
            setLiveGenSoftScore(null);
            setLiveGenMaximizeScore(null);
            // A stopped run publishes no final report, so nothing else would
            // ever clear this — the panel would keep claiming to be live over
            // metrics that stopped moving.
            setIsGenSummaryLive(false);
            showNotification(MSG_GEN_STOP[lang], NOTIFICATION.INFO);
            return;
        }

        const validity = describeInitialBuildValidity(build, {
            reqLvl: lvl,
            lockedSlots: genConfig?.lockedSlots || {},
        });
        if (validity?.blocking) return;

        const { req, timeLimit } = GenService.buildGenRequest(build, genConfig, DEFAULT_LVL);
        trackEvent('generate');
        // Counted here, where a run is certain to start, so the tally matches
        // what the person actually generated.
        pendingNudgeCountRef.current = isAuthenticated ? 0 : noteGuestGeneration();
        setLiveGenSoftScore(null);
        setLiveGenMaximizeScore(null);
        setTopCandidates(null);
        setTopSetCandidates(null);
        setGenSummaryOpen(false);
        setGenSummaryExpanded(false);
        setLiveMetrics(null);
        setFinalMetrics(null);
        setScoreTimeSeries([]);
        setElitePool(null);
        setWorkerMetrics(null);
        setAggregateMetrics(null);
        setIsGenSummaryLive(true);

        await startGenerationJob({
            req,
            timeLimit,
            // Deliberately the wall time the user picked, not `timeLimit`: that is
            // GenConfig.searchBudgetSeconds, which peels off the 4s a server solve
            // spent outside its search. The browser's overhead is ~0.4s, so reusing
            // that budget stopped a 7s request at 3s with the bar half full.
            attemptLocal: ({ signal, onLocalStart, onSearchStart, onFinalize, onFallback }) => solveBuild(build, genConfig, {
                seconds: Number(genConfig?.timeLimit) || GenConfig.TIME_LIMIT_DEFAULT,
                signal,
                onFallback,
                onStart: onLocalStart,
                onSearching: onSearchStart,
                onFinalize,
                onProgress: ({ best, metrics, scoreTimeSeries: series }) => {
                    ingestLiveScores({ score: best?.soft });
                    // The report fills in as the run goes.
                    if (metrics) {
                        setLiveMetrics(metrics);
                        if (series) setScoreTimeSeries(series);
                        setGenSummaryOpen(true);
                    }
                },
                onPreview: (payload) => {
                    // A run that already finished must not be overwritten by an
                    // interim build still in flight.
                    if (isGenerationActiveRef.current) applyBuildFromPayload(payload, build, false);
                },
                onReport: publishLocalReport,
            }),
            onDone: ({ result }) => {
                ingestLiveScores({ result });
                if (result?.build) applyBuildFromPayload(result.build, build, true);
                setIsGenSummaryLive(false);
                showNotification(
                    MSG_GEN_STREAM_END[lang] + ` ${formatGenerationScore(result?.score)}`,
                    NOTIFICATION.SUCCESS,
                );
                const nudgeCount = pendingNudgeCountRef.current;
                pendingNudgeCountRef.current = 0;
                if (nudgeCount) showLoginNudge(nudgeCount);
            },
            onError: (err) => {
                const status = Number(err?.status ?? err?.response?.status ?? 0);
                if (err?.code === 'LOGIN_REQUIRED') {
                    const isWeekly = err?.period === 'week';
                    const limit = Number(err?.limit) || (isWeekly ? GUEST_WEEKLY_LIMIT_DEFAULT : GUEST_LIMIT_DEFAULT);
                    showCapGate(isWeekly ? 'week' : 'day', limit);
                    return;
                }
                if (err?.code === 'DAILY_QUOTA') {
                    showNotification(MSG_DAILY_QUOTA[lang], NOTIFICATION.ERROR, QUOTA_NOTIF_DURATION_MS);
                    return;
                }
                if (err?.code === CODE_SERVER_SOLVE_OFF) {
                    // Not an incapable browser but an outdated page: the islands
                    // never answered because the chunk they live in was deployed
                    // away. "Reload" is the useful thing to say.
                    if (String(err?.reason || '').startsWith(WORKER_SCRIPT_UNAVAILABLE)) {
                        noteLocalSolverLost();
                        notifyStaleLocalSolve(showNotification, lang);
                        return;
                    }
                    showNotification(MSG_SERVER_SOLVE_OFF[lang], NOTIFICATION.ERROR, QUOTA_NOTIF_DURATION_MS);
                    return;
                }
                if ([429, 502, 503, 504].includes(status)) {
                    showNotification(MSG_SERVICE_UNAVAILABLE[lang], NOTIFICATION.ERROR);
                    return;
                }
                if (err?.networkError || /network error|failed to fetch/i.test(String(err?.message || ''))) {
                    showNotification(MSG_SERVER_UNREACHABLE[lang], NOTIFICATION.ERROR);
                    return;
                }
                showNotification(`${MSG_GEN_ERROR[lang]}: ${err?.message || 'Unknown error'}`, NOTIFICATION.ERROR);
            },
        });
    }, [
        canGenerate,
        isGenerationActive,
        stopGenerationJob,
        showNotification,
        lang,
        build,
        lvl,
        genConfig,
        startGenerationJob,
        ingestLiveScores,
        applyBuildFromPayload,
        formatGenerationScore,
        showCapGate,
        showLoginNudge,
        isAuthenticated,
        publishLocalReport,
    ]);

    const handleStop = useCallback(async () => {
        if (!isGenerationActive) return;
        await stopGenerationJob();
        setLiveGenSoftScore(null);
        setLiveGenMaximizeScore(null);
        setIsGenSummaryLive(false);
        showNotification(MSG_GEN_STOP[lang], NOTIFICATION.INFO);
    }, [isGenerationActive, stopGenerationJob, showNotification, lang]);

    return {
        canGenerate,
        generate,
        handleStop,
        liveGenSoftScore,
        liveGenMaximizeScore,
        topCandidates,
        topSetCandidates,
        genSummaryOpen,
        setGenSummaryOpen,
        genSummaryExpanded,
        setGenSummaryExpanded,
        liveMetrics,
        finalMetrics,
        scoreTimeSeries,
        elitePool,
        workerMetrics,
        aggregateMetrics,
        isGenSummaryLive,
    };
}

export default useGenerationRun;
